import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { THREAD_ID_STORAGE_KEY } from '../constants/storage';
import type { Message, ChatWidgetProps, SessionConfig } from '../types';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import { getSessionId } from '../utils/session';

interface UseThreadOptions {
  threadIdProp: string | null | undefined;
  threadIdControlled?: boolean;
  threadEndpoint: string | null | undefined;
  messageEndpoint: string | null | undefined;
  apiBaseUrl: string | null | undefined;
  apiKey: string | null | undefined;
  widgetAuthToken?: string | null;
  orgId?: string | null;
  agentId?: string | null;
  provider?: string;
  channel: string;
  threadCreateEndpoint: string | null | undefined;
  threadUpdateEndpoint: string | null | undefined;
  threadDeleteEndpoint: string | null | undefined;
  threadPollIntervalMs: number;
  persistThread: boolean;
  lazyLoad?: boolean;
  minimized: boolean;
  subject: string;
  department: string;
  customFields: Record<string, unknown>;
  metaExtras?: Record<string, unknown>;
  sessionConfig?: SessionConfig;
  buildThreadUrl?: ChatWidgetProps['buildThreadUrl'];
  buildThreadRequest?: ChatWidgetProps['buildThreadRequest'];
  buildThreadUpdateUrl?: ChatWidgetProps['buildThreadUpdateUrl'];
  buildThreadDeleteUrl?: ChatWidgetProps['buildThreadDeleteUrl'];
  parseThreadResponse?: ChatWidgetProps['parseThreadResponse'];
  parseThreadCreateResponse?: ChatWidgetProps['parseThreadCreateResponse'];
  onThreadCreate?: ChatWidgetProps['onThreadCreate'];
  onThreadUpdate?: ChatWidgetProps['onThreadUpdate'];
  onThreadDelete?: ChatWidgetProps['onThreadDelete'];
  trackAnalytics: (action: string, data: Record<string, unknown>) => void;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
}

function computeUrlWithId(base: string | null | undefined, id: string): string | null {
  if (!base) return null;
  const eid = encodeURIComponent(id);
  if (base.includes('{id}')) return base.replace('{id}', eid);
  if (base.includes(':id')) return base.replace(':id', eid);
  return `${base.replace(/\/$/, '')}/${eid}`;
}

function getThreadStorage(sessionConfig?: SessionConfig): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    if (sessionConfig?.storage === 'session') return window.sessionStorage;
    return window.localStorage;
  } catch {
    return null;
  }
}

export function useThread(options: UseThreadOptions) {
  const {
    threadIdProp,
    threadIdControlled = false,
    threadEndpoint,
    messageEndpoint,
    apiBaseUrl,
    apiKey,
    widgetAuthToken,
    orgId,
    agentId,
    provider,
    channel,
    threadCreateEndpoint,
    threadUpdateEndpoint,
    threadDeleteEndpoint,
    threadPollIntervalMs,
    persistThread,
    lazyLoad,
    minimized,
    subject,
    department,
    customFields,
    metaExtras,
    sessionConfig,
    buildThreadUrl,
    buildThreadRequest,
    buildThreadUpdateUrl,
    buildThreadDeleteUrl,
    parseThreadResponse,
    parseThreadCreateResponse,
    onThreadCreate,
    onThreadUpdate,
    onThreadDelete,
    trackAnalytics,
    setMessages,
  } = options;

  const [threadId, setThreadId] = useState<string | null>(() => {
    if (threadIdProp) return threadIdProp;
    if (!persistThread) return null;
    const storage = getThreadStorage(sessionConfig);
    if (!storage) return null;
    try {
      return storage.getItem(THREAD_ID_STORAGE_KEY);
    } catch {
      return null; // storage unavailable
    }
  });

  const isMountedRef = useRef(true);
  const threadIdRef = useRef<string | null>(threadId);
  const fetchInFlightRef = useRef(false);
  const pendingFetchRef = useRef(false);
  const activeFetchControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    threadIdRef.current = threadId;
  }, [threadId]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      pendingFetchRef.current = false;
      activeFetchControllerRef.current?.abort();
      activeFetchControllerRef.current = null;
    };
  }, []);

  // Keep internal threadId in sync with prop
  useEffect(() => {
    if (!threadIdControlled) return;
    if (threadIdProp !== threadId) setThreadId(threadIdProp || null);
  }, [threadIdProp, threadIdControlled, threadId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist thread id locally
  useEffect(() => {
    if (!persistThread) return;
    const storage = getThreadStorage(sessionConfig);
    if (!storage) return;
    try {
      if (threadId) storage.setItem(THREAD_ID_STORAGE_KEY, threadId);
      else storage.removeItem(THREAD_ID_STORAGE_KEY);
    } catch {
      // Storage unavailable
    }
  }, [threadId, persistThread, sessionConfig]);

  // Infer a sensible thread base endpoint
  const threadBase = useMemo(() => {
    if (threadEndpoint) return threadEndpoint;
    if (messageEndpoint) {
      try {
        const trimmedMessageEndpoint = messageEndpoint.trim();
        const windowOrigin =
          typeof window !== 'undefined' && window.location?.origin ? window.location.origin : null;
        const u = windowOrigin
          ? new URL(trimmedMessageEndpoint, windowOrigin)
          : new URL(trimmedMessageEndpoint);

        if (!['http:', 'https:'].includes(u.protocol)) return null;

        let basePath = u.pathname.replace(/\/$/, '');
        if (basePath.endsWith('/api/response/stateset')) {
          basePath = basePath.replace(/\/stateset$/, '/threads');
        } else if (basePath.endsWith('/api/chat')) {
          basePath = basePath.replace(/\/chat$/, '/threads');
        } else if (basePath.endsWith('/api/response')) {
          basePath = `${basePath}/threads`;
        } else {
          basePath = '/api/threads';
        }
        return `${u.origin}${basePath}`;
      } catch {
        return null; // messageEndpoint is not a valid URL
      }
    }
    if (apiBaseUrl) return `${apiBaseUrl.replace(/\/$/, '')}/threads`;
    return null;
  }, [threadEndpoint, messageEndpoint, apiBaseUrl]);

  const withAuthHeaders = useCallback(
    (headers: Record<string, string> = {}): Record<string, string> => ({
      'Content-Type': 'application/json',
      ...(apiKey ? { Authorization: `Bearer ${apiKey}` } : {}),
      ...(widgetAuthToken ? { 'x-widget-token': widgetAuthToken } : {}),
      ...(orgId ? { 'x-org-id': orgId } : {}),
      ...(agentId ? { 'x-agent-id': agentId } : {}),
      ...headers,
    }),
    [apiKey, widgetAuthToken, orgId, agentId]
  );

  const fetchThreadRef = useRef<() => Promise<void>>(async () => {});

  // Fetch thread messages
  const fetchThread = useCallback(async () => {
    if (!threadBase || !threadId) return;
    if (fetchInFlightRef.current) {
      pendingFetchRef.current = true;
      return;
    }
    const requestedThreadId = threadId;
    const controller = new AbortController();
    fetchInFlightRef.current = true;
    activeFetchControllerRef.current = controller;
    try {
      const url =
        typeof buildThreadUrl === 'function'
          ? buildThreadUrl({ threadId: requestedThreadId, endpoint: threadBase, channel })
          : (() => {
              const base = threadBase.replace(/\/$/, '');
              const q = channel ? `?channel=${encodeURIComponent(channel)}` : '';
              return `${base}/${encodeURIComponent(requestedThreadId)}${q}`;
            })();
      const defaultReq: RequestInit = {
        method: 'GET',
        headers: withAuthHeaders(),
      };
      const reqInit =
        typeof buildThreadRequest === 'function'
          ? buildThreadRequest({ threadId: requestedThreadId, endpoint: threadBase })
          : defaultReq;
      const res = await fetchWithTimeout(url, {
        ...reqInit,
        signal: controller.signal,
        timeoutMs: 15_000,
      });
      if (!res.ok) throw new Error(`Thread fetch failed: ${res.status}`);
      const data = await res.json();
      if (controller.signal.aborted || !isMountedRef.current) return;
      const parsed = typeof parseThreadResponse === 'function' ? parseThreadResponse(data) : null;
      let serverMessages = parsed?.messages;
      if (!serverMessages) {
        const raw = data?.messages || data?.thread?.messages || [];
        if (Array.isArray(raw)) {
          serverMessages = raw.map((m: Record<string, unknown>, i: number) => ({
            id: (m.id ?? m.messageId ?? `${requestedThreadId}-${i}`) as string,
            sender:
              (typeof m.sender === 'string' && m.sender.toLowerCase() === 'user') ||
              (typeof m.role === 'string' && m.role.toLowerCase() === 'user')
                ? ('user' as const)
                : ('ai' as const),
            text: (m.text ?? m.message ?? m.content ?? '') as string,
            timestamp: m.timestamp
              ? new Date(m.timestamp as string).toLocaleTimeString()
              : new Date().toLocaleTimeString(),
            status: 'delivered' as const,
            type: (m.type ?? m.messageType) as Message['type'],
            threadSource: String(
              typeof m.sender === 'string' ? m.sender : typeof m.role === 'string' ? m.role : ''
            ).trim(),
            threadRole: String(typeof m.role === 'string' ? m.role : '').trim(),
            richContent: (m.richContent ?? m.rich_content) as Message['richContent'],
            suggestions: Array.isArray(m.suggestions) ? (m.suggestions as string[]) : undefined,
            sources: Array.isArray(m.sources) ? (m.sources as Message['sources']) : undefined,
            toolCalls: Array.isArray(m.toolCalls)
              ? (m.toolCalls as Message['toolCalls'])
              : undefined,
            thinking: m.thinking as string | undefined,
          }));
        }
      }
      // Ignore stale responses that return after thread switch.
      if (threadIdRef.current !== requestedThreadId) return;
      if (serverMessages && serverMessages.length >= 0) {
        setMessages(serverMessages);
        trackAnalytics('thread_fetch', { ok: true, count: serverMessages.length });
      }
    } catch (err) {
      const isAbortError =
        controller.signal.aborted || (err instanceof DOMException && err.name === 'AbortError');
      if (isAbortError) return;
      trackAnalytics('thread_fetch', {
        ok: false,
        error: err instanceof Error ? err.message : 'unknown',
      });
    } finally {
      if (activeFetchControllerRef.current === controller) {
        activeFetchControllerRef.current = null;
      }
      fetchInFlightRef.current = false;
      if (pendingFetchRef.current && isMountedRef.current) {
        pendingFetchRef.current = false;
        void Promise.resolve().then(() => {
          if (isMountedRef.current) {
            void fetchThreadRef.current();
          }
        });
      }
    }
  }, [
    threadBase,
    threadId,
    buildThreadUrl,
    buildThreadRequest,
    parseThreadResponse,
    trackAnalytics,
    channel,
    setMessages,
    withAuthHeaders,
  ]);

  // Stable ref so polling doesn't restart when fetchThread identity changes
  useEffect(() => {
    fetchThreadRef.current = fetchThread;
  }, [fetchThread]);

  // Abort stale request when closing widget to avoid background updates.
  useEffect(() => {
    if (minimized) {
      pendingFetchRef.current = false;
      activeFetchControllerRef.current?.abort();
    }
  }, [minimized]);

  // Fetch on open or when thread changes
  useEffect(() => {
    if (!minimized) fetchThread();
  }, [minimized, fetchThread]);

  useEffect(() => {
    if (lazyLoad && minimized) return;
    fetchThread();
  }, [threadId, threadBase, lazyLoad, minimized]); // eslint-disable-line react-hooks/exhaustive-deps

  // Optional polling (use ref-stable fetchThread to prevent interval restarts)
  useEffect(() => {
    if (!threadPollIntervalMs || threadPollIntervalMs <= 0) return;
    if (minimized) return;
    if (!threadId) return;
    const id = setInterval(() => {
      fetchThreadRef.current();
    }, threadPollIntervalMs);
    return () => clearInterval(id);
  }, [threadPollIntervalMs, minimized, threadId]);

  const createThread = useCallback(
    async ({
      subject: s,
      message: m,
      meta,
      ch,
    }: {
      subject?: string;
      message?: string;
      meta?: Record<string, unknown>;
      ch?: string;
    } = {}) => {
      const endpoint = threadCreateEndpoint;
      if (!endpoint) throw new Error('threadCreateEndpoint is not configured');
      const payload = {
        subject: s || subject || 'New conversation',
        message: m || '',
        ...(provider ? { provider } : {}),
        meta: { department, customFields, ...(metaExtras || {}), ...(meta || {}) },
        sessionId: getSessionId(sessionConfig),
        source: 'response-chat-widget',
        channel: ch || channel,
      };
      const res = await fetchWithTimeout(endpoint, {
        method: 'POST',
        headers: withAuthHeaders(),
        body: JSON.stringify(payload),
        timeoutMs: 15_000,
      });
      if (!res.ok) {
        let serverError = '';
        try {
          const payload = (await res.json()) as Record<string, unknown>;
          if (typeof payload?.error === 'string') serverError = payload.error;
          else if (typeof payload?.message === 'string') serverError = payload.message;
        } catch {
          // Keep generic status code message if body is not JSON.
        }
        throw new Error(
          `Thread create failed: ${res.status}${serverError ? ` (${serverError})` : ''}`
        );
      }
      const data = await res.json();
      const parsed =
        typeof parseThreadCreateResponse === 'function' ? parseThreadCreateResponse(data) : null;
      const newId = parsed?.threadId ?? data?.threadId ?? data?.id ?? data?.thread?.id;
      if (newId) setThreadId(newId);
      onThreadCreate?.(newId, data);
      trackAnalytics('thread_create', { ok: !!newId });
      if (m) {
        const now = new Date();
        setMessages(prev => [
          ...prev,
          {
            id: `${now.getTime()}`,
            sender: 'user' as const,
            text: m,
            timestamp: now.toLocaleTimeString(),
            status: 'sent' as const,
          },
        ]);
      }
      if (newId) {
        try {
          await fetchThread();
        } catch {
          // Thread refresh after create is best-effort; failure is non-critical
        }
      }
      return { threadId: newId, raw: data };
    },
    [
      threadCreateEndpoint,
      subject,
      department,
      customFields,
      metaExtras,
      provider,
      channel,
      onThreadCreate,
      withAuthHeaders,
      trackAnalytics,
      parseThreadCreateResponse,
      fetchThread,
      setMessages,
      sessionConfig,
    ]
  );

  const updateThread = useCallback(
    async (id: string, patch: Record<string, unknown> = {}) => {
      const url = buildThreadUpdateUrl
        ? buildThreadUpdateUrl({ id, endpoint: threadUpdateEndpoint || threadBase || '' })
        : computeUrlWithId(threadUpdateEndpoint || threadBase, id);
      if (!url) throw new Error('No thread update URL configured');
      const res = await fetchWithTimeout(url, {
        method: 'PATCH',
        headers: withAuthHeaders(),
        body: JSON.stringify(patch),
        timeoutMs: 15_000,
      });
      if (!res.ok) throw new Error(`Thread update failed: ${res.status}`);
      const data = await res.json();
      onThreadUpdate?.(id, data);
      trackAnalytics('thread_update', { ok: true });
      return data;
    },
    [
      threadUpdateEndpoint,
      threadBase,
      buildThreadUpdateUrl,
      withAuthHeaders,
      onThreadUpdate,
      trackAnalytics,
    ]
  );

  const deleteThread = useCallback(
    async (id: string) => {
      const url = buildThreadDeleteUrl
        ? buildThreadDeleteUrl({ id, endpoint: threadDeleteEndpoint || threadBase || '' })
        : computeUrlWithId(threadDeleteEndpoint || threadBase, id);
      if (!url) throw new Error('No thread delete URL configured');
      const res = await fetchWithTimeout(url, {
        method: 'DELETE',
        headers: withAuthHeaders(),
        timeoutMs: 15_000,
      });
      const ok = res.ok;
      try {
        await res.json();
      } catch {
        // DELETE responses may have empty body; safe to ignore JSON parse failure
      }
      if (threadId === id) {
        setThreadId(null);
        setMessages([]);
      }
      onThreadDelete?.(id);
      trackAnalytics('thread_delete', { ok });
      return { ok };
    },
    [
      threadDeleteEndpoint,
      threadBase,
      buildThreadDeleteUrl,
      withAuthHeaders,
      threadId,
      onThreadDelete,
      trackAnalytics,
      setMessages,
    ]
  );

  return {
    threadId,
    setThreadId,
    threadBase,
    fetchThread,
    createThread,
    updateThread,
    deleteThread,
    withAuthHeaders,
  };
}
